import { createConsumer, type Subscription } from '@rails/actioncable';

// Create a single consumer to be shared across the app
const isCableEnabled = () => {
  try {
    // Prefer Vite flag, fallback to global flag (no Node typings required)
    const viteFlag = (import.meta as ImportMeta)?.env?.VITE_ENABLE_CABLE;
    if (typeof viteFlag !== 'undefined') return String(viteFlag) === 'true';
  } catch {}
  try {
    type GlobalWithFlag = { ENABLE_CABLE?: unknown };
    const g = (typeof globalThis !== 'undefined' ? (globalThis as unknown) : {}) as GlobalWithFlag;
    if (typeof g.ENABLE_CABLE !== 'undefined') return String(g.ENABLE_CABLE) === 'true';
  } catch {}
  return false;
};

function isDev(): boolean {
  try {
    return typeof import.meta !== 'undefined' && !!(import.meta as ImportMeta).env?.DEV;
  } catch {
    return false;
  }
}

// Lazy consumer - created on first use to allow test configuration before import
let _consumer: ReturnType<typeof createConsumer> | null | undefined;

const getConsumer = () => {
  if (_consumer !== undefined) return _consumer;

  if (!isCableEnabled() || typeof window === 'undefined') {
    _consumer = null;
    return _consumer;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const url = `${protocol}//${host}/cable`;
  if (isDev()) console.log('Connecting to ActionCable at:', url);

  _consumer = createConsumer(url);
  return _consumer;
};

// For testing: allow injecting a mock consumer
export function setConsumerForTesting(consumer: ReturnType<typeof createConsumer> | null) {
  _consumer = consumer;
}

// Export a function to subscribe to a channel
export function subscribeToTaskUpdates<T = unknown>(
  taskId: number,
  callbacks: {
    connected?: () => void;
    disconnected?: () => void;
    received: (data: T) => void;
  },
): Subscription {
  const consumer = getConsumer();
  if (!consumer) {
    console.log('ActionCable consumer not available');
    return { unsubscribe() {} } as unknown as Subscription;
  }

  if (isDev()) {
    console.log('Subscribing to task updates for task ID:', taskId);
  }

  return consumer.subscriptions.create(
    {
      channel: 'TaskUpdatesChannel',
      id: taskId.toString(),
    },
    {
      connected: () => {
        if (isDev()) {
          console.log(`Connected to TaskUpdatesChannel for task ${taskId}`);
        }
        if (callbacks.connected) callbacks.connected();
      },
      disconnected: () => {
        if (isDev()) {
          console.log(`Disconnected from TaskUpdatesChannel for task ${taskId}`);
        }
        if (callbacks.disconnected) callbacks.disconnected();
      },
      received: (data: unknown) => {
        if (isDev()) {
          console.log(`Received update for task ${taskId}:`, data);
        }
        callbacks.received(data as T);
      },
    },
  );
}

// Export the consumer getter in case it's needed directly
export { getConsumer as default };

// Subscribe to appointments broadcasted on 'appointments' stream
export function subscribeToAppointments<T = unknown>(callbacks: {
  connected?: () => void;
  disconnected?: () => void;
  received: (data: T) => void;
}) {
  const consumer = getConsumer();
  if (!consumer) {
    console.log('ActionCable consumer not available');
    return { unsubscribe() {} } as unknown as import('@rails/actioncable').Subscription;
  }
  return consumer.subscriptions.create(
    { channel: 'AppointmentsChannel' },
    {
      connected: () => {
        callbacks.connected?.();
      },
      disconnected: () => {
        callbacks.disconnected?.();
      },
      received: (data: unknown) => {
        callbacks.received(data as T);
      },
    },
  );
}

// Subscribe to a professional's availability stream
export type AvailabilityUpdateType = 'AVAILABILITY_UPDATED' | 'SLOT_OCCUPIED' | 'SLOT_AVAILABLE';

export interface AvailabilityUpdatePayload {
  type: AvailabilityUpdateType;
  professional_id: number;
  availability_schedule?: { day: number; timeSlots: string[] }[];
  schedule_mode?: 'weekly' | 'dynamic';
  schedule_days_ahead?: number;
  dynamic_availability_schedule?: { date: string; timeSlots: string[] }[];
  appointment_id?: number;
  datetime?: string;
  duration_blocks?: number;
  end_time?: string;
}

export function subscribeToProfessionalAvailability(
  professionalId: number,
  callbacks: {
    connected?: () => void;
    disconnected?: () => void;
    received: (data: AvailabilityUpdatePayload) => void;
  },
) {
  const consumer = getConsumer();
  if (!consumer) {
    console.log('ActionCable consumer not available');
    return { unsubscribe() {} } as unknown as import('@rails/actioncable').Subscription;
  }

  return consumer.subscriptions.create(
    { channel: 'ProfessionalAvailabilityChannel', id: String(professionalId) },
    {
      connected: () => {
        callbacks.connected?.();
      },
      disconnected: () => {
        callbacks.disconnected?.();
      },
      received: (data: unknown) => {
        // Support both AVAILABILITY_UPDATED (full schedule) and SLOT_OCCUPIED (single slot)
        const payload = data as Partial<AvailabilityUpdatePayload>;
        if (!payload || typeof payload.professional_id !== 'number') {
          console.warn('Ignored unexpected availability payload:', data);
          return;
        }

        if (
          payload.type === 'AVAILABILITY_UPDATED' &&
          (Array.isArray(payload.availability_schedule) ||
            Array.isArray(payload.dynamic_availability_schedule) ||
            typeof payload.schedule_mode === 'string')
        ) {
          console.log(
            `[ActionCable] AVAILABILITY_UPDATED received for professional ${payload.professional_id}:`,
            payload,
          );
          callbacks.received(payload as AvailabilityUpdatePayload);
        } else if (payload.type === 'SLOT_OCCUPIED' && payload.datetime) {
          console.log(
            `[ActionCable] SLOT_OCCUPIED received for professional ${payload.professional_id}:`,
            payload,
          );
          callbacks.received(payload as AvailabilityUpdatePayload);
        } else if (payload.type === 'SLOT_AVAILABLE' && payload.datetime) {
          console.log(
            `[ActionCable] SLOT_AVAILABLE received for professional ${payload.professional_id}:`,
            payload,
          );
          callbacks.received(payload as AvailabilityUpdatePayload);
        } else {
          console.warn('Ignored unexpected availability payload:', data);
        }
      },
    },
  );
}

export interface FakeChatCableMessage {
  id: number;
  sender: 'client' | 'staff';
  body: string;
  created_at: string;
}

// Subscribe to the fake-chat widget stream. Staff replies from BioBot are
// broadcast to `fake_chat_<token>` and received here in real time.
export function subscribeToFakeChat(
  token: string,
  callbacks: {
    connected?: () => void;
    disconnected?: () => void;
    received: (data: FakeChatCableMessage) => void;
  },
) {
  const consumer = getConsumer();
  if (!consumer) {
    console.log('ActionCable consumer not available');
    return { unsubscribe() {} } as unknown as import('@rails/actioncable').Subscription;
  }
  return consumer.subscriptions.create(
    { channel: 'FakeChatChannel', token },
    {
      connected: () => {
        callbacks.connected?.();
      },
      disconnected: () => {
        callbacks.disconnected?.();
      },
      received: (data: unknown) => {
        const msg = data as Partial<FakeChatCableMessage>;
        if (!msg || typeof msg.sender !== 'string' || typeof msg.body !== 'string') {
          console.warn('Ignored unexpected fake-chat payload:', data);
          return;
        }
        callbacks.received(data as FakeChatCableMessage);
      },
    },
  );
}
